import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  FileText,
  HelpCircle,
  LogOut,
  ScrollText,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION } from '../constants/appVersion';
import { PAGE_CHROME_OFF_WHITE } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import { AccountProfileCard } from './account/AccountProfileCard';
import { ACCOUNT_PRIVACY_URL, ACCOUNT_TERMS_URL } from './account/accountSettingsContent';

const DELETE_REDIRECT_SECONDS = 10;
const DELETE_CONFIRM_PHRASE = 'delete my account';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[13px] font-semibold uppercase tracking-wider text-theme-text/45 dark:text-theme-off-white/45 mb-1.5 px-1">
      {children}
    </h2>
  );
}

const listCard =
  'rounded-xl border border-theme-border/80 dark:border-white/10 bg-white dark:bg-white/[0.04] overflow-hidden';

function MenuLinkRow({
  to,
  icon: Icon,
  label,
  iconStroke = 1.75,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  iconStroke?: number;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3.5 text-sm text-theme-text dark:text-theme-off-white border-b border-theme-border/50 dark:border-white/[0.06] last:border-b-0 hover:bg-black/[0.025] dark:hover:bg-white/[0.04] transition-colors"
    >
      <Icon className="w-[18px] h-[18px] text-theme-text dark:text-theme-off-white shrink-0" strokeWidth={iconStroke} />
      <span className="flex-1">{label}</span>
      <ChevronRight className="w-4 h-4 text-theme-text dark:text-theme-off-white shrink-0" strokeWidth={2} />
    </Link>
  );
}

function MenuExternalRow({
  href,
  icon: Icon,
  label,
  iconStroke = 1.75,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  iconStroke?: number;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 px-4 py-3.5 text-sm text-theme-text dark:text-theme-off-white border-b border-theme-border/50 dark:border-white/[0.06] last:border-b-0 hover:bg-black/[0.025] dark:hover:bg-white/[0.04] transition-colors"
    >
      <Icon className="w-[18px] h-[18px] text-theme-text dark:text-theme-off-white shrink-0" strokeWidth={iconStroke} />
      <span className="flex-1">{label}</span>
      <ChevronRight className="w-4 h-4 text-theme-text dark:text-theme-off-white shrink-0" strokeWidth={2} />
    </a>
  );
}

export const Account = () => {
  usePageStatusBar({ background: PAGE_CHROME_OFF_WHITE });
  const navigate = useNavigate();
  const { profile, user, signOut, deleteMemberAccount } = useAuth();

  const [showDeleteInfo, setShowDeleteInfo] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [confirmPhraseInput, setConfirmPhraseInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteRedirectSecondsLeft, setDeleteRedirectSecondsLeft] = useState<number | null>(null);
  const deleteRedirectFiredRef = useRef(false);

  useEffect(() => {
    if (!deleteSuccess || deleteRedirectSecondsLeft === null) return;

    if (deleteRedirectSecondsLeft === 0) {
      if (deleteRedirectFiredRef.current) return;
      deleteRedirectFiredRef.current = true;
      void (async () => {
        await signOut();
        navigate('/auth');
      })();
      return;
    }

    const id = window.setTimeout(() => {
      setDeleteRedirectSecondsLeft((s) => (s === null || s <= 0 ? s : s - 1));
    }, 1000);
    return () => clearTimeout(id);
  }, [deleteSuccess, deleteRedirectSecondsLeft, navigate, signOut]);

  const accountEmail = profile?.email ?? user?.email ?? '';

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const openDeleteInfo = () => {
    setDeleteError(null);
    setDeleteSuccess(false);
    setDeleteRedirectSecondsLeft(null);
    deleteRedirectFiredRef.current = false;
    setShowDeleteInfo(true);
  };

  const closeDeleteInfo = () => setShowDeleteInfo(false);

  const proceedFromInfoToConfirm = () => {
    setShowDeleteInfo(false);
    setDeleteError(null);
    setDeleteSuccess(false);
    setDeleteRedirectSecondsLeft(null);
    deleteRedirectFiredRef.current = false;
    setDeleteStep(1);
    setConfirmPhraseInput('');
    setShowDeleteConfirm(true);
  };

  const closeDeleteModal = () => {
    if (deleting || deleteSuccess) return;
    setShowDeleteConfirm(false);
    setDeleteStep(1);
    setConfirmPhraseInput('');
    setDeleteError(null);
    setDeleteSuccess(false);
    setDeleteRedirectSecondsLeft(null);
    deleteRedirectFiredRef.current = false;
  };

  const phraseMatches = confirmPhraseInput.trim() === DELETE_CONFIRM_PHRASE;

  const handleConfirmDeleteAccount = async () => {
    if (!accountEmail || !phraseMatches) {
      setDeleteError(
        !accountEmail
          ? 'Account email is still loading. Try again in a moment.'
          : `Type exactly: ${DELETE_CONFIRM_PHRASE} (case- and spacing-sensitive).`,
      );
      return;
    }
    setDeleteError(null);
    setDeleting(true);
    const { error } = await deleteMemberAccount();
    setDeleting(false);
    if (error) {
      setDeleteError(error);
      return;
    }
    setDeleteSuccess(true);
    setDeleteRedirectSecondsLeft(DELETE_REDIRECT_SECONDS);
  };

  return (
    <div className="min-h-screen flex flex-col bg-theme-off-white dark:bg-theme-bg-dark overflow-x-hidden pb-bottom-nav">
      <header className="flex-shrink-0 px-2 h-14 flex items-center gap-1 border-b border-theme-border/60 dark:border-white/10">
        <button
          type="button"
          onClick={() => navigate('/member-dashboard')}
          className="p-2 rounded-lg text-theme-text dark:text-theme-off-white hover:bg-theme-muted/50 dark:hover:bg-white/10"
          aria-label="Back to dashboard"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-theme-text dark:text-theme-off-white tracking-tight">Account</h1>
      </header>

      <div className="flex-1 px-4 pt-4 pb-6 max-w-lg mx-auto w-full">
        <AccountProfileCard className="mb-6" />

        <div className="space-y-5">
          <section>
            <SectionLabel>Support</SectionLabel>
            <div className={listCard}>
              <MenuLinkRow to="/account/help-feedback" icon={HelpCircle} label="Help & Feedback" />
              <MenuExternalRow href={ACCOUNT_PRIVACY_URL} icon={FileText} label="Privacy Policy" />
              <MenuExternalRow href={ACCOUNT_TERMS_URL} icon={ScrollText} label="Terms of Service" />
            </div>
          </section>

          <section>
            <SectionLabel>Connect</SectionLabel>
            <div className={listCard}>
              <MenuLinkRow to="/account/communication-preferences" icon={Bell} label="Communication preferences" />
              <MenuLinkRow to="/account/invite-friends" icon={UserPlus} label="Invite friends" />
            </div>
          </section>

          <section>
            <SectionLabel>Session</SectionLabel>
            <div className={listCard}>
              <button
                type="button"
                onClick={() => void handleSignOut()}
                className="flex items-center gap-3 w-full px-4 py-3.5 text-sm text-theme-text dark:text-theme-off-white border-b-0 hover:bg-black/[0.025] dark:hover:bg-white/[0.04] transition-colors text-left"
              >
                <LogOut className="w-[18px] h-[18px] text-theme-text dark:text-theme-off-white shrink-0" strokeWidth={1.75} />
                <span className="flex-1 font-medium">Sign out</span>
              </button>
            </div>
          </section>

          <section>
            <SectionLabel>Danger zone</SectionLabel>
            <div className={`${listCard} border-red-200/60 dark:border-red-400/20`}>
              <button
                type="button"
                onClick={openDeleteInfo}
                disabled={!accountEmail}
                className="flex items-center gap-3 w-full px-4 py-3.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50/80 dark:hover:bg-red-950/20 transition-colors disabled:opacity-45 disabled:cursor-not-allowed text-left"
              >
                <Trash2 className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
                <span className="flex-1 font-medium">Delete account</span>
              </button>
            </div>
          </section>
        </div>

        <p className="text-center text-xs text-theme-text/40 dark:text-theme-off-white/35 mt-10 px-4">
          Version {APP_VERSION}
        </p>
      </div>

      {showDeleteInfo && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-theme-text/50 dark:bg-black/60 backdrop-blur-sm p-4"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDeleteInfo();
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-theme-border dark:border-white/15 bg-white dark:bg-white/10 p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-info-title"
          >
            <div className="relative mb-5 flex min-h-10 items-center justify-center">
              <button
                type="button"
                onClick={closeDeleteInfo}
                className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full p-2 text-theme-text/55 transition-colors hover:bg-theme-muted/50 hover:text-theme-text dark:text-theme-off-white/60 dark:hover:bg-white/10 dark:hover:text-theme-off-white"
                aria-label="Cancel"
              >
                <X className="h-5 w-5" strokeWidth={2.25} aria-hidden />
              </button>
              <h3
                id="delete-account-info-title"
                className="px-10 text-center text-lg font-heading font-semibold text-theme-text dark:text-theme-off-white"
              >
                Delete account
              </h3>
            </div>
            <p className="mb-5 text-center text-sm text-theme-text/80 dark:text-theme-off-white/80">
              This will permanently delete your Bravos member account. You&apos;ll be signed out, your profile data will
              be removed, and you won&apos;t be able to log back in. Re-registering with the same email will create a
              fresh account.
            </p>
            <button
              type="button"
              onClick={proceedFromInfoToConfirm}
              className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[101] flex items-end sm:items-center justify-center bg-theme-text/50 dark:bg-black/60 backdrop-blur-sm p-4"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleting && !deleteSuccess) {
              closeDeleteModal();
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-theme-border dark:border-white/15 bg-white dark:bg-white/10 p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-confirm-title"
          >
            {deleteSuccess ? (
              <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
                <h3
                  id="delete-account-confirm-title"
                  className="text-lg font-heading font-semibold text-theme-text dark:text-theme-off-white"
                >
                  Account deleted
                </h3>
                <p className="mt-3 text-sm text-theme-text/90 dark:text-theme-off-white/90">
                  Your account is deleted! You&apos;ll be redirected to the login page
                  {deleteRedirectSecondsLeft !== null && deleteRedirectSecondsLeft > 0 ? (
                    <>
                      {' '}
                      in{' '}
                      <span className="font-semibold tabular-nums text-theme-text dark:text-theme-off-white">
                        {deleteRedirectSecondsLeft}
                      </span>{' '}
                      second{deleteRedirectSecondsLeft === 1 ? '' : 's'}.
                    </>
                  ) : (
                    '.'
                  )}
                </p>
              </div>
            ) : (
              <>
                <div className="relative mb-5 flex min-h-10 items-center justify-center">
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={closeDeleteModal}
                    className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full p-2 text-theme-text/55 transition-colors hover:bg-theme-muted/50 hover:text-theme-text disabled:opacity-50 dark:text-theme-off-white/60 dark:hover:bg-white/10 dark:hover:text-theme-off-white"
                    aria-label="Cancel"
                  >
                    <X className="h-5 w-5" strokeWidth={2.25} aria-hidden />
                  </button>
                  <h3
                    id="delete-account-confirm-title"
                    className="px-10 text-center text-lg font-heading font-semibold text-theme-text dark:text-theme-off-white"
                  >
                    {deleteStep === 1 ? 'Are you sure?' : 'Confirm deletion'}
                  </h3>
                </div>
                {deleteStep === 1 ? (
                  <>
                    <p className="mb-5 text-center text-sm text-theme-text/80 dark:text-theme-off-white/80">
                      This cannot be undone. On the next step you will type a short phrase to confirm deletion.
                    </p>
                    <button
                      type="button"
                      disabled={deleting}
                      onClick={() => {
                        setDeleteError(null);
                        setDeleteStep(2);
                      }}
                      className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Continue
                    </button>
                  </>
                ) : (
                  <>
                    <div className="mb-4 space-y-3 text-center">
                      <p className="text-sm text-theme-text/80 dark:text-theme-off-white/80">
                        Type the following exactly (comparison is case-sensitive):
                      </p>
                      <div className="flex justify-center">
                        <span className="inline-flex max-w-full items-center rounded-full border border-theme-border bg-white px-3 py-1.5 font-mono text-xs font-semibold text-neutral-900 dark:border-neutral-500/50">
                          {DELETE_CONFIRM_PHRASE}
                        </span>
                      </div>
                      <label htmlFor="delete-confirm-phrase" className="sr-only">
                        Type delete my account to confirm account deletion
                      </label>
                      <input
                        id="delete-confirm-phrase"
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        value={confirmPhraseInput}
                        onChange={(e) => setConfirmPhraseInput(e.target.value)}
                        className="w-full rounded-xl border border-theme-border bg-white px-3 py-2.5 text-left text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-red-500/40 dark:border-white/20 dark:bg-black/20 dark:text-theme-off-white"
                        aria-invalid={confirmPhraseInput.length > 0 && !phraseMatches}
                      />
                    </div>
                    {deleteError && (
                      <p className="mb-4 text-center text-sm text-red-600 dark:text-red-400" role="alert">
                        {deleteError}
                      </p>
                    )}
                    <button
                      type="button"
                      disabled={deleting || !phraseMatches}
                      onClick={handleConfirmDeleteAccount}
                      className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium leading-snug text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {deleting ? 'Closing account…' : 'Delete my account permanently'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
